/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { useWheel } from './useWheel';
import { Range, pan } from '../utils/panUtils';
import { useUser } from '../../hooks/UNSAFE_useUser';

type UseWheelPanOptions = {
  width: number;
  height: number;
  getRangeX: () => Range;
  getRangeY: () => Range;
  globalRangeX: Range;
  globalRangeY: Range;
  onRangeXChange: (detail: Range) => void;
  onRangeYChange: (detail: Range) => void;
  isDisabled?: boolean;
};

/**
 * Hook that enables and handles wheel pan gestures.
 */
const useWheelPan = ({
  width,
  height,
  getRangeX,
  getRangeY,
  globalRangeX,
  globalRangeY,
  onRangeXChange,
  onRangeYChange,
  isDisabled = false
}: UseWheelPanOptions) => {
  const { direction } = useUser();
  const prevRangeX = useRef({ min: 0, max: 0 });
  const prevRangeY = useRef({ min: 0, max: 0 });

  const wheelProps = useWheel({
    onWheel: ({ deltaX: dx, deltaY: dy, ctrlKey }) => {
      // WheelEvent with ctrlKey means pinch/zoom gesture
      if (ctrlKey) return;

      const { newRange: newRangeX } = pan(
        ((direction === 'rtl' ? -1 : 1) * dx) / width,
        getRangeX(),
        prevRangeX.current,
        globalRangeX,
        onRangeXChange
      );
      prevRangeX.current = newRangeX;

      const { newRange: newRangeY } = pan(
        dy / height,
        getRangeY(),
        prevRangeY.current,
        globalRangeY,
        onRangeYChange
      );
      prevRangeY.current = newRangeY;
    },
    isDisabled
  });

  return isDisabled ? {} : wheelProps;
};

export { useWheelPan };
